import type { ModelInstance } from './instanced-layout';

/**
 * The lobby character stage's set, built from the game's own dungeon pack.
 *
 * User: "can we make this live stage match the game's aesthetic (maybe even
 * using game assets) and have the character standing on the ground instead of
 * floating in the air?"
 *
 * Both halves of that came from the same cause. The stage rendered the
 * character alone onto a transparent canvas, over a generated painterly plate
 * of an arched alcove (`assets/ui/lobby/alcove.jpg`) — so there was no floor to
 * stand on, and the backdrop was a different art style from the low-poly figure
 * in front of it. The seam was visible precisely because the character was
 * right.
 *
 * The fix is to stop painting a dungeon and build one: `wall`,
 * `floor_tile_large` and `torch` are the exact models `prefabs.ts` uses for
 * every room in the game, laid out here as plain `ModelInstance` data and fed
 * to `buildInstancedLayout` — the same function the dungeon scene itself calls.
 * There is no second art pipeline to keep in sync, and no per-class or per-skin
 * plate to redraw.
 *
 * Layout data rather than a builder so the geometry is unit-testable without
 * WebGL: "does the floor actually reach where the camera looks" is arithmetic,
 * and it is the part that goes wrong.
 */

/**
 * KayKit's module size, and the reason every coordinate here is a multiple of
 * 4. Mirrors `GRID_UNIT_M` in the backend's `prefabs.ts`; kept as a local
 * constant because there is no shared package between backend and frontend
 * (same convention as `character-roster.ts`).
 */
export const GRID_UNIT_M = 4;

/**
 * The top face of `floor_tile_large`, measured off the model: it spans
 * y −0.1 … +0.05, so its surface is 0.05 above its own origin.
 *
 * This single number is what "standing on the ground instead of floating" comes
 * down to. A character whose bounding box starts at y=0 (which KayKit's do)
 * placed at y=0 stands 5cm *inside* the tile; placed on the tile's surface it
 * stands on it.
 */
export const FLOOR_SURFACE_Y = 0.05;

/** Where the back wall's front face sits. Walls are 1 unit deep, centred on their origin. */
const WALL_ROW_Z = -2.5;
const WALL_FACE_Z = WALL_ROW_Z + 0.5;

/**
 * Floor extent, in tiles either side of the character.
 *
 * Three columns and three rows, because the camera sits about 7 units back
 * (`computeStageCamera` against a 2.54-unit model) and looks slightly down: a
 * single tile would end well inside the frame and show void at the bottom
 * edge, which reads worse than no floor at all. Every tile is the same model,
 * so all nine cost one download and one draw call.
 */
const FLOOR_COLUMNS = [-1, 0, 1];
const FLOOR_ROWS = [0, 1, 2];

/**
 * The set, as instance data.
 *
 * Composition deliberately echoes the plate it replaces — a lit archway with a
 * sconce either side — because that framing worked; it is the *art* that was
 * off-style, not the staging.
 */
export const STAGE_SET_INSTANCES: readonly ModelInstance[] = [
  // Floor, extending toward the camera.
  ...FLOOR_ROWS.flatMap((row) => FLOOR_COLUMNS.map((col): ModelInstance => ({
    model: 'floor_tile_large',
    x: col * GRID_UNIT_M,
    y: 0,
    z: row * GRID_UNIT_M,
    rotationY: 0,
  }))),

  // The back wall. `wall`'s depth runs along z already, so an unrotated
  // segment faces the camera and needs no rotation.
  { model: 'wall_arched', x: 0, y: 0, z: WALL_ROW_Z, rotationY: 0 },
  { model: 'wall', x: -GRID_UNIT_M, y: 0, z: WALL_ROW_Z, rotationY: 0 },
  { model: 'wall', x: GRID_UNIT_M, y: 0, z: WALL_ROW_Z, rotationY: 0 },

  // Closes the arch. Without it the opening is a hole onto the clear colour,
  // which reads as a bug rather than as a passage; one unlit segment behind it
  // reads as a recess the light does not reach.
  { model: 'wall', x: 0, y: 0, z: WALL_ROW_Z - GRID_UNIT_M, rotationY: 0 },

  // Side walls, angled in. Only their inner faces are ever on screen; they
  // exist so the frame's edges are stone rather than void when the panel is
  // wide.
  { model: 'wall', x: -2 * GRID_UNIT_M, y: 0, z: WALL_ROW_Z + GRID_UNIT_M, rotationY: Math.PI / 2 },
  { model: 'wall', x: 2 * GRID_UNIT_M, y: 0, z: WALL_ROW_Z + GRID_UNIT_M, rotationY: Math.PI / 2 },

  // The sconces, mounted either side of the arch. `torch`'s own origin sits
  // above its bracket, so this is the bracket height, not the flame's.
  { model: 'torch', x: -1.7, y: 2.1, z: WALL_FACE_Z, rotationY: 0 },
  { model: 'torch', x: 1.7, y: 2.1, z: WALL_FACE_Z, rotationY: 0 },
];

/** Where the torch flames are, for the point lights that go with them. */
export const TORCH_LIGHT_POSITIONS: readonly (readonly [number, number, number])[] = [
  [-1.7, 2.7, WALL_FACE_Z + 0.2],
  [1.7, 2.7, WALL_FACE_Z + 0.2],
];

/**
 * The furthest point on the floor from the character, toward the camera.
 * Exposed so a test can assert the floor reaches past where the camera looks.
 */
export function floorExtentZ(): number {
  return Math.max(...FLOOR_ROWS) * GRID_UNIT_M + GRID_UNIT_M / 2;
}

/** How wide the floor is, either side of the character. */
export function floorHalfWidth(): number {
  return Math.max(...FLOOR_COLUMNS) * GRID_UNIT_M + GRID_UNIT_M / 2;
}
